import type { CampaignType, SearchResult, TemplateType } from '@/services/auth/types';
import { TEMPLATE_TYPE_LABELS } from '../constants';

export type GroupType =
  | Exclude<SearchResult['kind'], 'template'>
  | Exclude<TemplateType, 'unset'>
  | Extract<CampaignType, 'triggered_broadcast'>;

type SearchResultKindDescriptor = {
  kind: SearchResult['kind'];
  heading: string;
  hasQuickLookPreview: boolean;
};

export const SEARCH_RESULT_KIND_METADATA = {
  customer: { heading: 'People', hasQuickLookPreview: true },
  campaign: { heading: 'Campaigns', hasQuickLookPreview: true },
  segment: { heading: 'Segments', hasQuickLookPreview: true },
  action: { heading: 'Actions', hasQuickLookPreview: true },
  navigation: { heading: 'Navigation', hasQuickLookPreview: false },
  newsletter: { heading: 'Newsletters', hasQuickLookPreview: true },
  goal: { heading: 'Goals', hasQuickLookPreview: true },
  transactional_message: { heading: 'Transactional Messages', hasQuickLookPreview: true },
  anonymous_in_app: { heading: 'Anonymous In-App Messages', hasQuickLookPreview: true },
  snippet: { heading: 'Snippets', hasQuickLookPreview: true },
  email_layout: { heading: 'Email Layouts', hasQuickLookPreview: true },
  collection: { heading: 'Collections', hasQuickLookPreview: true },
  docs: { heading: 'Documentation', hasQuickLookPreview: true },
  source: { heading: 'Sources', hasQuickLookPreview: true },
  destination: { heading: 'Destinations', hasQuickLookPreview: true },
  ad_audience: { heading: 'Ad Audiences', hasQuickLookPreview: false },
  custom_form: { heading: 'Forms', hasQuickLookPreview: true },
  reporting_webhook: { heading: 'Reporting Webhooks', hasQuickLookPreview: false },
  cdp_out: { heading: 'CDP Event Forwarding', hasQuickLookPreview: false },
  data_out: { heading: 'Data-Out', hasQuickLookPreview: false },
  object_type: { heading: 'Object Types', hasQuickLookPreview: true },
  template: { heading: 'Templates', hasQuickLookPreview: true },
} as const satisfies Record<SearchResult['kind'], Omit<SearchResultKindDescriptor, 'kind'>>;

const KINDS_WITH_DESCRIPTION_FIELD = [
  'campaign',
  'segment',
  'goal',
  'docs',
  'transactional_message',
  'anonymous_in_app',
  'collection',
] as const;
type KindWithDescriptionField = (typeof KINDS_WITH_DESCRIPTION_FIELD)[number];

export function hasDescriptionField(
  item: SearchResult,
): item is Extract<SearchResult, { kind: KindWithDescriptionField }> {
  return (KINDS_WITH_DESCRIPTION_FIELD as readonly string[]).includes(item.kind);
}

export const SEARCH_RESULT_KIND_DESCRIPTORS = Object.entries(SEARCH_RESULT_KIND_METADATA).map(
  ([kind, metadata]) =>
    ({
      kind,
      ...metadata,
    }) as SearchResultKindDescriptor,
);

type DefinedSearchResultKindDescriptor = (typeof SEARCH_RESULT_KIND_DESCRIPTORS)[number];
type GroupableSearchResultKindDescriptor = Exclude<DefinedSearchResultKindDescriptor, { kind: 'template' }>;

function isGroupableSearchResultKindDescriptor(
  descriptor: DefinedSearchResultKindDescriptor,
): descriptor is GroupableSearchResultKindDescriptor {
  return descriptor.kind !== 'template';
}

function buildHeadingRecord<const T extends readonly { kind: string; heading: string }[]>(
  descriptors: T,
): Record<T[number]['kind'], T[number]['heading']> {
  return Object.fromEntries(descriptors.map(({ kind, heading }) => [kind, heading])) as Record<
    T[number]['kind'],
    T[number]['heading']
  >;
}

const SEARCH_RESULT_KIND_HEADINGS = buildHeadingRecord(
  SEARCH_RESULT_KIND_DESCRIPTORS.filter(isGroupableSearchResultKindDescriptor),
);

export const SEARCH_RESULT_HEADINGS = {
  ...SEARCH_RESULT_KIND_HEADINGS,
  triggered_broadcast: 'API Triggered Broadcasts',
  // Template results are grouped by template_type rather than their result kind.
  ...TEMPLATE_TYPE_LABELS,
} as const satisfies Record<GroupType, string>;

/**
 * Resolves the display heading for a result group. The `customer` and `newsletter`
 * groups are taxonomy-gated (People → Profiles, Newsletters → One-time sends), so
 * their resolved terms are passed in; all other groups use the static heading map.
 */
export function getResultGroupHeading(
  kind: GroupType,
  taxonomyHeadings: { customer: string; newsletter: string },
): string {
  if (kind === 'customer') return taxonomyHeadings.customer;
  if (kind === 'newsletter') return taxonomyHeadings.newsletter;
  return SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`;
}
